import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  TrendingDown,
  Activity,
  AlertCircle,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ReplacementAssessmentPanel } from '@/components/equipment/ReplacementAssessmentPanel';
import {
  PageHeader,
  MetricCard,
  KPIGrid,
  LoadingState,
  DataTable,
  Column,
} from '@/components/common';
import { rotableApi, type ReplacementAssessment, UtilizationMetrics } from '@/api/services';
import { cn } from '@/lib/utils';
import { RecordMovementDialog } from '@/components/equipment/RecordMovementDialog';

// Status colors (currently unused)
// const statusColors = {
//   IN_USE: 'bg-status-success-subtle text-status-success-foreground border-status-success-border',
//   IN_YARD: 'bg-status-info-subtle text-status-info-foreground border-status-info-border',
//   IN_REPAIR: 'bg-status-warning-subtle text-status-warning-foreground border-status-warning-border',
//   SCRAPPED: 'bg-status-neutral-subtle text-status-neutral-foreground border-border',
// };

export default function RotableAssets() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [utilizationFilter, setUtilizationFilter] = useState<string>('all');

  const [allAssets, setAllAssets] = useState<UtilizationMetrics[]>([]);
  const [underutilized, setUnderutilized] = useState<UtilizationMetrics[]>([]);
  const [assessments, setAssessments] = useState<ReplacementAssessment[]>([]);
  const [movementFor, setMovementFor] = useState<UtilizationMetrics | null>(null);
  const [filteredAssets, setFilteredAssets] = useState<UtilizationMetrics[]>([]);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [allData, underutilizedData, assessmentData] = await Promise.all([
        rotableApi.getUtilizationMetrics(),
        rotableApi.getUnderutilizedAssets(30),
        // The replace count comes from the three-argument assessment, not from
        // the superseded score. Two answers to one question on one page is how
        // a reader decides neither is trustworthy.
        rotableApi.getReplacementAssessment(),
      ]);
      setAllAssets(allData);
      setUnderutilized(underutilizedData);
      setAssessments(assessmentData);
    } catch (error) {
      console.error('Failed to load rotable assets:', error);
      toast.error('Failed to load rotable assets data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Apply filter
  const applyFilters = useCallback(() => {
    let data = allAssets;

    // Apply tab filter
    if (activeTab === 'underutilized') {
      data = underutilized;
    } else if (activeTab === 'replacement') {
      // Rendered by ReplacementAssessmentPanel, which loads its own rows.
      data = [];
    }

    // Apply utilization filter
    if (utilizationFilter !== 'all') {
      // An asset whose rate could not be computed matches no band. It is
      // unmeasured, not idle, and putting it in the low bucket is how a busy
      // asset ends up on a disposal list.
      const rate = (a: UtilizationMetrics) => a.utilizationRate;
      if (utilizationFilter === 'low') {
        data = data.filter((a) => rate(a) != null && rate(a)! < 30);
      } else if (utilizationFilter === 'medium') {
        data = data.filter((a) => rate(a) != null && rate(a)! >= 30 && rate(a)! < 70);
      } else if (utilizationFilter === 'high') {
        data = data.filter((a) => rate(a) != null && rate(a)! >= 70);
      } else if (utilizationFilter === 'unmeasured') {
        data = data.filter((a) => rate(a) == null);
      }
    }

    setFilteredAssets(data);
  }, [activeTab, allAssets, underutilized, utilizationFilter]);

  useEffect(() => {
    applyFilters();
  }, [activeTab, utilizationFilter, allAssets, underutilized, applyFilters]);

  /** Assets the assessment puts past a threshold, or approaching one. */
  const needsAttention = assessments.filter(
    (a) => a.verdict === 'REPLACE' || a.verdict === 'REVIEW'
  ).length;

  const calculateSummary = () => {
    if (allAssets.length === 0) {
      return {
        totalAssets: 0,
        measuredCount: 0,
        outNowCount: 0,
        // Null, not zero: with no assets there is nothing to average.
        avgUtilization: null as number | null,
        totalValue: 0,
        underutilizedCount: 0,
        replacementNeededCount: 0,
      };
    }

    const totalValue = allAssets.reduce((sum, a) => sum + Number(a.totalCost), 0);
    // Averaged over the assets that have a rate, not over all of them. An
    // unmeasured asset counted as zero drags the fleet figure down and makes
    // the pool look idle when it is only undocumented.
    const measured = allAssets.filter((a) => a.utilizationRate != null);
    const avgUtilization = measured.length
      ? measured.reduce((sum, a) => sum + (a.utilizationRate ?? 0), 0) / measured.length
      : null;

    return {
      totalAssets: allAssets.length,
      measuredCount: measured.length,
      outNowCount: allAssets.filter((a) => a.outNow).length,
      avgUtilization,
      totalValue,
      underutilizedCount: underutilized.length,
      replacementNeededCount: needsAttention,
    };
  };

  if (loading) {
    return <LoadingState />;
  }

  const summary = calculateSummary();

  const columns: Column<UtilizationMetrics>[] = [
    {
      key: 'asset',
      linkTo: (asset) => `/equipment/${asset.equipmentId}`,
      header: 'Asset',
      render: (asset) => (
        <div>
          <div className="font-medium">{asset.name}</div>
          <div className="text-sm text-muted-foreground font-mono">{asset.assetNumber}</div>
        </div>
      ),
    },
    {
      key: 'utilization',
      header: 'Utilization',
      sortable: true,
      accessorFn: (asset) => asset.utilizationRate ?? -1,
      render: (asset) => {
        // Null is its own reading, not a zero bar. An asset with no
        // acquisition date has an unknown denominator; drawing that as an
        // empty bar says it never worked.
        if (asset.utilizationRate == null) {
          return (
            <div>
              <span className="text-sm text-status-warning-foreground">Not measured</span>
              <p className="mt-0.5 text-xs text-muted-foreground">{asset.daysInUse} days out</p>
            </div>
          );
        }

        const rate = asset.utilizationRate;
        return (
          <div className="min-w-[8rem]">
            <div className="mb-1 flex items-baseline gap-2">
              <span className="text-lg font-semibold tabular-nums">{rate.toFixed(1)}%</span>
              {asset.outNow && (
                <Badge className="bg-status-info-subtle text-status-info-foreground">Out now</Badge>
              )}
            </div>
            <div className="h-2 w-full rounded-full bg-muted">
              <div
                className={cn(
                  'h-2 rounded-full',
                  rate < 30 && 'bg-status-destructive',
                  rate >= 30 && rate < 70 && 'bg-status-warning',
                  rate >= 70 && 'bg-status-success'
                )}
                style={{ width: `${Math.min(rate, 100)}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {asset.daysInUse} of {asset.daysAvailable} days · {asset.trips} trip
              {asset.trips === 1 ? '' : 's'}
            </p>
          </div>
        );
      },
    },
    {
      key: 'usage',
      header: 'Usage',
      render: (asset) => (
        <div className="text-sm">
          <div>
            <span className="font-medium">{asset.daysInUse}</span> days in use
          </div>
          <div className="text-muted-foreground">of {asset.daysAvailable} available</div>
        </div>
      ),
    },
    {
      key: 'cycles',
      header: 'Cycles',
      sortable: true,
      accessorFn: (asset) => asset.totalCycles,
      render: (asset) => (
        <div className="font-medium tabular-nums">{asset.totalCycles.toLocaleString()}</div>
      ),
    },
    {
      key: 'cost',
      header: 'Cost Analysis',
      render: (asset) => (
        <div className="text-sm">
          <div className="font-medium">
            $
            {Number(asset.costPerCycle).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            / cycle
          </div>
          <div className="text-muted-foreground">
            $
            {Number(asset.costPerDay).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            / day
          </div>
        </div>
      ),
    },
    {
      key: 'totalCost',
      header: 'Total Value',
      sortable: true,
      render: (asset) => (
        <div>
          <div className="font-bold">${Number(asset.totalCost).toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">
            Repairs: ${Number(asset.cumulativeRepairCost).toLocaleString()}
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (asset) => (
        <div className="flex items-center justify-end gap-1">
          {/* The only way in the product to record a run, and therefore the
              only way a cycle count ever changes. The endpoint has existed
              since the module was written and nothing called it. */}
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setMovementFor(asset);
            }}
          >
            Record movement
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/equipment/${asset.equipmentId}`)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Equipment', href: '/equipment' },
          { label: 'Rotable Assets' },
        ]}
        title="Rotable Assets"
        description="Repairable assets cycling through the pool, with utilisation, cycle count, and repair cost to date."
        actions={
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        }
      />

      {/* Four, not five. "Total Value" was reading acquisitionCost — null on
          every rotable — and reported $0.0M beside an average utilisation of
          0.0% flagged red. Capital tied up is an accounting question anyway;
          this page is about whether the pool is working. */}
      <KPIGrid columns={4}>
        <MetricCard
          label="Rotables"
          value={summary.totalAssets}
          icon={<Package className="h-5 w-5" />}
          sentiment="info"
          subtitle={
            summary.measuredCount < summary.totalAssets
              ? `${summary.totalAssets - summary.measuredCount} without an acquisition date`
              : 'all measurable'
          }
        />
        <MetricCard
          label="Out now"
          value={summary.outNowCount}
          icon={<Activity className="h-5 w-5" />}
          sentiment={summary.outNowCount > 0 ? 'success' : 'warning'}
          subtitle={
            summary.outNowCount > 0
              ? `${summary.totalAssets - summary.outNowCount} sitting in the yard`
              : 'the whole pool is idle'
          }
        />
        <MetricCard
          label="Fleet utilisation"
          // Null rather than 0.0% with a red flag. "We do not record this" and
          // "nothing is working" are different answers.
          value={
            summary.avgUtilization == null
              ? 'Not measured'
              : `${summary.avgUtilization.toFixed(1)}%`
          }
          icon={<TrendingDown className="h-5 w-5" />}
          sentiment={
            summary.avgUtilization == null
              ? 'neutral'
              : summary.avgUtilization >= 30
                ? 'success'
                : 'warning'
          }
          subtitle={
            summary.avgUtilization == null
              ? 'no asset has both a date and an assignment'
              : `of days owned, across ${summary.measuredCount} measured`
          }
        />
        <MetricCard
          label="Past a threshold"
          value={summary.replacementNeededCount}
          icon={<AlertCircle className="h-5 w-5" />}
          sentiment={summary.replacementNeededCount > 0 ? 'danger' : 'success'}
          subtitle="repair versus replace"
        />
      </KPIGrid>

      {/* Alerts */}
      {needsAttention > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <strong>{needsAttention} assets</strong> are past a replacement threshold, or
                approaching one
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('replacement')}>
                View All
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* The "Top Underutilized Assets" panel stood here and listed every
          asset in the pool, because every asset read 0.0%. It duplicated a tab
          while adding nothing the tab did not already say, and it was the
          largest single piece of the clutter on this page. */}

      {/* Main Table with Tabs.

          The card header that stood here held a heading reading "Asset
          Utilization Analysis" directly above a tab called "Utilisation" — two
          labels for one thing — and the utilisation filter. The filter has
          moved into the table's own control row, which it shares with Export
          and View; it only feeds the Utilisation tab, so up here it was also a
          live control sitting over a tab it does nothing to. With both gone the
          header was an empty padded box, so it went too. */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            {/* Two tabs, not three. "Underutilized" rendered the same table
                with the same rows — the filter above it already does that job,
                and a tab whose count equals the tab beside it teaches a reader
                that the counts mean nothing. */}
            <TabsList className="mb-4">
              <TabsTrigger value="all">Utilisation ({allAssets.length})</TabsTrigger>
              <TabsTrigger value="replacement">
                Repair vs replace ({assessments.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <DataTable
                columns={columns}
                data={filteredAssets}
                toolbar={
                  <Select value={utilizationFilter} onValueChange={setUtilizationFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by utilization" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Utilization Levels</SelectItem>
                      <SelectItem value="low">Low (0-30%)</SelectItem>
                      <SelectItem value="medium">Medium (30-70%)</SelectItem>
                      <SelectItem value="high">High (70-100%)</SelectItem>
                      <SelectItem value="unmeasured">Not measured</SelectItem>
                    </SelectContent>
                  </Select>
                }
                pagination
                exportable
                columnToggle
                emptyTitle="No rotables match"
                emptyDescription="Adjust the utilisation filter, or mark an asset rotable to see it here."
              />
            </TabsContent>

            <TabsContent value="replacement">
              {/* Not the generic asset table. This tab answers "should we keep
                  spending on this", and that answer is only worth anything with
                  the arithmetic beside it.

                  The explanation used to sit as a paragraph across the middle
                  of the page, explaining a calculation the reader had not
                  reached yet. It belongs next to what it explains. */}
              <p className="mb-4 text-sm text-muted-foreground">
                Three independent arguments: repair spend against acquisition cost, cycles against
                the class limit, and condition grade. The verdict is the strongest of them, never an
                average, because any one is sufficient on its own.
              </p>
              <ReplacementAssessmentPanel />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <RecordMovementDialog
        asset={movementFor}
        onClose={() => setMovementFor(null)}
        onRecorded={loadData}
      />
    </div>
  );
}
